import type { MeetingDebugEvent } from '@electron/shared/meeting-debug.types';

/** A meeting's own entries plus the global entries that led to its start, matched by detection or auto-start attempt id */
export function collectMeetingEvents(
  meetingEvents: MeetingDebugEvent[],
  globalEvents: MeetingDebugEvent[]
): MeetingDebugEvent[] {
  const detectionIds = new Set(meetingEvents.map((e) => e.detectionId));
  const attemptIds = new Set(meetingEvents.map((e) => e.autoStartAttemptId));
  detectionIds.delete(undefined);
  attemptIds.delete(undefined);

  const adopted = globalEvents.filter(
    (event) =>
      detectionIds.has(event.detectionId) ||
      attemptIds.has(event.autoStartAttemptId)
  );
  return [...adopted, ...meetingEvents].sort((a, b) => a.at - b.at);
}

export function formatInactivityCountdown({
  timeoutMinutes,
  lastActivityAt,
  nowMs,
}: {
  timeoutMinutes: number;
  lastActivityAt: Date | undefined;
  nowMs: number;
}): string {
  if (timeoutMinutes === 0 || !lastActivityAt) return 'off';
  const remainingSeconds = Math.max(
    0,
    Math.round(
      (lastActivityAt.getTime() + timeoutMinutes * 60_000 - nowMs) / 1000
    )
  );
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = String(remainingSeconds % 60).padStart(2, '0');
  return `${minutes}m ${seconds}s left of ${timeoutMinutes} min`;
}

/** Presence as the presence-loss stop sees it: the bound app against the latest detection */
export function describePresence(
  boundAppBundleId: string | undefined,
  latestDetection: MeetingDebugEvent | undefined
): string {
  if (!latestDetection) return 'no detection yet';
  const apps = (latestDetection.fields.apps ?? []) as {
    bundleIdentifier?: string;
  }[];
  if (apps.length === 0) return 'no meeting apps detected';
  if (!boundAppBundleId) return 'no bound app yet';
  const normalize = (bundleId?: string) => bundleId?.trim().toLowerCase();
  return apps.some(
    (app) => normalize(app.bundleIdentifier) === normalize(boundAppBundleId)
  )
    ? 'bound app detected'
    : 'bound app missing';
}
